import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, FormControl, UntypedFormGroup, FormArray, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, forkJoin, merge } from 'rxjs';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { IPendingTransaction } from 'src/app/shared/interfaces/pending-transaction.interface';
import { NavigatedFromPipe } from 'src/app/shared/pipes/navigated-from/navigated-from.pipe';
import { TransactionsState } from 'src/app/shared/states/transactions/transactions.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BulkVerify, BulkVerifyValidateNdcTransactionDate, GetPendingTransactionsForBulkVerify, RefreshPendingTransactionDetails, SetCarouselDisplayList } from 'src/app/shared/states/transactions/transactions.actions';
import { IBulkVerifyPendingTransaction } from '../../interfaces/IBulkVerifyPendingTransaction';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { PinService } from 'src/app/shared/services/pin.service';
import { IBulkVerifyRequest } from '../../interfaces/IBulkVerifyRequest';
import { distinctUntilChanged, skip, take } from 'rxjs/operators';
import { AlertTypesEnum } from 'src/app/shared/interfaces/AlertTypesEnum';
import { getUTC, groupByProperty, toTitleCase } from 'src/app/shared/helpers/helpers';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { IBulkVerifyPendingTransactionRow } from '../../interfaces/IBulkVerifyPendingTransactionRow';
import { DATE_MAX_STR, DATE_MIN_STR } from 'src/app/shared/constants/constants';
import { getEndOfDay, getDateRange } from 'src/app/shared/helpers/date-helpers';
import { SearchDateRange } from 'src/app/shared/interfaces/SearchDateRange';
import { IDateRange } from 'src/app/shared/interfaces/IDateRange';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { IntegrationTypeEnum } from 'src/app/shared/interfaces/integration.interface';
import { BaseTableDirective } from 'src/app/shared/directives/base-table.directive';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { BulkVerifyHubService } from 'src/app/shared/services/bulk-verify-hub.service';
import { ButtonIconPositions } from 'src/app/shared/interfaces/button-icon-position.enum';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import Decimal from 'decimal.js';

@UntilDestroy()
@Component({
  selector: 'app-bulk-verify',
  templateUrl: './bulk-verify.component.html',
  styleUrls: ['./bulk-verify.component.scss'],
})
export class BulkVerifyComponent extends BaseTableDirective implements OnInit, OnDestroy {
  navigation: any;
  navigationState: any;

  SearchInputColorEnum = SearchInputColorEnum;

  cardsForm: UntypedFormGroup;

  ButtonIconPositions = ButtonIconPositions;
  
  @Select(TransactionsState.loading) loading$: Observable<boolean | undefined>;
  @Select(TransactionsState.pendingTransactions) pendingTransactions$: Observable<IPendingTransaction[]>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(TransactionsState.bulkVerifyInProgress) bulkVerifyInProgress$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  hiddenTransactions: {id: string, position: number}[] = [];

  showCannotUpdateTransactionDate: boolean;
  cannotUpdateTrDateText: string;

  pinDialogChannel: string = "BULK_VERIFY";

  statuses = [];
  pharmacists = [];

  isStatusesDropdownOpened: boolean;
  statusItemsShowLimit = 2;
  isPharmacistsDropdownOpened: boolean;
  pharmacistItemsShowLimit = 2;
  isTrDateDropdownOpened: boolean;

  statusListSettings = {
    selectAllText: 'All Statuses',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Statuses',
    noDataAvailablePlaceholderText: 'No statues available',
  };

  pharmacistListSettings = {
    selectAllText: 'All Pharmacists',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Pharmacists',
    noDataAvailablePlaceholderText: 'No pharmacists available',
  };

  @ViewChild('statusesMultiOptionalDropdown') statusesMultiOptionalDropdown;
  @ViewChild('pharmacistsMultiOptionalDropdown') pharmacistsMultiOptionalDropdown;
  @ViewChild('schedulesMultiOptionalDropdown') schedulesMultiOptionalDropdown;

  IntegrationTypeEnum = IntegrationTypeEnum;
  transactionsIntegrationType: IntegrationTypeEnum;

  isSearchShrunk: boolean = true;

  tableColumns = {
    rxNumber: false,
    refillNumber: false,
    patientName: false,
    statusCodeDate: true,
    friendlyName: false,
    startingQuantity: false,
    addedQuantity: false,
    endingQuantity: false,
    similarOnHand: false
  }

  locationsToExclude: string[] = [];

  constructor(
    private router: Router,
    private navigatedFromPipe: NavigatedFromPipe,
    protected store: Store,
    private fb: UntypedFormBuilder,
    private formattedDatePipe: FormattedDatePipe,
    private pinService: PinService, private cd: ChangeDetectorRef,
    private alertService: AlertMessageService,
    public hubService: BulkVerifyHubService
  ) {
    super(store);
    this.navigation = this.router.getCurrentNavigation();
    this.resetForm();
  }

  ngOnInit(): void {
    this.navigationState = this.navigation?.extras?.state;
    this.getData();

    this.inventoryLocationsEnabled$
    .pipe(untilDestroyed(this))
    .subscribe(enabled => {
      if (enabled) {
        this.locationsToExclude = [this.store.selectSnapshot(InventoryState.damagedExpiredLocationID)];
      }
    });

    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.dialogId === this.pinDialogChannel && res?.success) {
        this.bulkVerify(res.pin);
      }
    })

    const filters$ = merge(
      this.cardsForm.get('period').valueChanges,
      this.cardsForm.get('customPeriod').valueChanges,
      this.cardsForm.get('statuses').valueChanges,
      this.cardsForm.get('pharmacists').valueChanges,
      this.cardsForm.get('schedules').valueChanges,
      this.cardsForm.get('search').valueChanges
    );

    filters$
    .pipe(distinctUntilChanged(), untilDestroyed(this))
    .subscribe(x => {
      if (x || !this.cardsForm.controls.search.value) {
        setTimeout(() => {
          this.applyFilters();
        });
      }
    })

    this.cardsForm.get('inventoryLocationId').valueChanges
    .pipe(distinctUntilChanged(), untilDestroyed(this), skip(1))
    .subscribe(val => {
      this.getData(val);
    });
    //Start websocket connection to catch an event when somebody else initiates the bulk-verify
    if (!this.hubService.isConnected()) {
      this.hubService.startListening();
    }
  }

  ngOnDestroy(): void {
    if (this.hubService.isConnected()) {
      this.hubService.ngOnDestroy();
    }
  }

  private getData(locationID?: string) {
    this.store.dispatch(new GetPendingTransactionsForBulkVerify(locationID))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.clearTransactions();
      const transactions = this.store.selectSnapshot(TransactionsState)?.bulkVerifyPendingTransactions || [];
      this.populateFilters(transactions);
      this.transactionsIntegrationType = transactions[0]?.integrationType;
      transactions.map(x => this.appendTransaction(x));
      groupByProperty((transactions as IBulkVerifyPendingTransaction[]).filter(x => !!x.gpi), 'gpi')?.map(x => this.adjustSOH(x.header));
    });
  }

  private adjustSOH(gpi: string): void {
   const rows = (this.cardsForm.controls.transactions as FormArray<FormGroup<FormControls<IBulkVerifyPendingTransactionRow>>>)
    .controls.filter(control => control.value.gpi === gpi && control.value.shouldShow);
    if (rows?.length) {
        const actualSOH = new Decimal(+rows[0].value.similarOnHand);
        let newSOH = rows
        .map(x => +x.value.addedQuantity)
        .reduce((acc, count) => acc.sub(new Decimal(count)), actualSOH)
        .toNumber();
  
        rows.forEach(control => {
          const value = control.value;
          if (!!value.gpi) {
            control.get('availableSOH').setValue(newSOH);
          }
        });
    }
  }

  private populateFilters(transactions: IBulkVerifyPendingTransaction[]): void {
    this.statuses = groupByProperty(transactions, 'status')?.map(x => x?.header);
    this.pharmacists = groupByProperty(transactions, 'pharmacist')?.map(x => toTitleCase(x?.header));
  }

  dateTimeChanged(evt, index) {
    const dateTime = new Date(evt?.value);
    const control = this.getTransactionControl(index);
    const oldTrDate = control.value.statusCodeDate;
    control.patchValue({...control.value, statusCodeDate: dateTime});
    const transactions = (this.cardsForm.controls.transactions as UntypedFormArray).value as IBulkVerifyPendingTransaction[];
    const drugId = transactions[index].drugId;
    const payload = {
      drugId: drugId,
      locationId: this.cardsForm.controls?.inventoryLocationId?.value,
      items: transactions
      .filter(x => x.drugId === drugId)
      .map(x => ({id: x.id, addedQuantity: x.addedQuantity, startingQuantity: x.startingQuantity, endingQuantity: x.endingQuantity, statusCodeDate: x.statusCodeDate}))
    };

    this.store.dispatch(new BulkVerifyValidateNdcTransactionDate(payload)).subscribe(res => {
      const response = this.store.selectSnapshot(TransactionsState.response) as any;
      if (response?.isNotEnoughInventoryError) {
        this.displayCannotUpdateTrDateAlert("Not enough inventory on this date.");
        this.resetTrDateBackToOldValue(control, oldTrDate);
      } else if (response?.isTransactionDateBeyondLastAuditDateError) {
        this.displayCannotUpdateTrDateAlert("Cannot confirm this transaction because transaction date/time is older than 'audit type' on 'audit date'.");
        this.resetTrDateBackToOldValue(control, oldTrDate);
      } else if (response?.isAmendTransactionDateBeforeOriginalTransactionError) {
        this.displayCannotUpdateTrDateAlert("Cannot confirm this transaction because transaction date/time is older than the amend transaction on 'amend date/time'.");
        this.resetTrDateBackToOldValue(control, oldTrDate);
      }
       else if (response.items) {
        response?.items?.forEach(element => {
        const control = this.getTransactionControlById(element.id);
        control.patchValue({...control.value, startingQuantity: element.startingQuantity, endingQuantity: element.endingQuantity});
        });
        this.orderByTransactionDateAsc();
      }
    })

  }

  refresh(idx: number): void {
    const controlValue = this.getTransactionControl(idx).value;
    this.store.dispatch(new RefreshPendingTransactionDetails(controlValue.id))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      const isCardRemoved = this.store.selectSnapshot(TransactionsState).currentPendingTransactionRemoved;
      if(!isCardRemoved) {
        //TODO update card
      } else {
        //TODO show alert and remove
      }
    });

  }

  bulkVerify(pin: number): void {
    const transactions = this.cardsForm.value.transactions?.filter(x => x.shouldShow && !x.filteredOut) as IBulkVerifyPendingTransaction[];
    if (!transactions.length) {
      console.error("Nothing to verify");
      return;
    }

    this.pinService.closeDialog();

    const payload: IBulkVerifyRequest = {
      pin: pin,
      items: transactions.map(x => ({id: x.id, transactionDate: x.statusCodeDate, locationId: this.cardsForm.get('inventoryLocationId')?.value}))
    }
    
   
    this.store.dispatch(new BulkVerify(payload))
    .pipe(take(1))
    .subscribe(x => {
      const response = this.store.selectSnapshot(TransactionsState.response);
      if (response?.success) {
        this.store.dispatch(new SetCarouselDisplayList([]));
        this.navigateBack();
      }
    });
  }

  private resetTrDateBackToOldValue(control: AbstractControl, oldTrDate: string): void {
    control.patchValue({
      statusCodeDate: oldTrDate, 
      transactionDateDisplayValue: this.formattedDatePipe.transform(oldTrDate, 'MMM d a, HH:mm yyyy')
    })
    control.updateValueAndValidity();
  }

  private displayCannotUpdateTrDateAlert(reason: string): void {
    this.showCannotUpdateTransactionDate = true;
    this.cannotUpdateTrDateText = reason;
  }

  private orderByTransactionDateAsc(): void {
    const transactions = this.cardsForm.controls.transactions as UntypedFormArray;
    const sortedTransactions = [...transactions.controls.sort((a, b) => {
      const dateA = (a as UntypedFormGroup).get('statusCodeDate').value;
      const dateB = (b as UntypedFormGroup).get('statusCodeDate').value;

      return new Date(dateA).getTime() - new Date(dateB).getTime();
    })];

   this.clearTransactions();
   sortedTransactions.map(x => this.appendTransaction(x.value));
  }

  private appendTransaction(transaction: IBulkVerifyPendingTransactionRow): void {
    const transactions = this.cardsForm.controls.transactions as UntypedFormArray;
    let transactionDate = new Date(transaction.statusCodeDate);
    if (transaction.integrationType === IntegrationTypeEnum.LibertyRx) {
      transactionDate = getUTC(transaction.statusCodeDate);
    }
    const packageNdc = transaction.packageNdcs?.[0] || (transaction as any).packageNdc;
    const transactionToAdd = this.fb.group({
      id: transaction.id,
      rxNumber: transaction.rxNumber,
      partialNumber: transaction.partialNumber,
      refillNumber: transaction.refillNumber,
      friendlyName: transaction.friendlyName,
      packageNdc: packageNdc,
      packageNdcNoDash: packageNdc.replaceAll('-', ''),
      patientName: toTitleCase(transaction.patientName),
      startingQuantity: transaction.startingQuantity || 0,
      addedQuantity: transaction.addedQuantity,
      endingQuantity: +transaction.startingQuantity - +transaction.addedQuantity,
      similarOnHand: transaction.similarOnHand,
      availableSOH: transaction.similarOnHand,
      gpi: transaction.gpi,
      transactionDateDisplayValue: this.formattedDatePipe.transform(transactionDate, 'MMM d a, HH:mm yyyy'),
      drugId: transaction.drugId,
      statusCodeDate: transactionDate,
      status: transaction.status,
      pharmacist: toTitleCase(transaction.pharmacist),
      schedule: transaction.schedule,
      shouldShow: transaction.shouldShow ?? true,
      filteredOut: transaction.filteredOut ?? false
    });
   transactions.push(transactionToAdd);
  }

  private clearTransactions(): void {
    (this.cardsForm.get('transactions') as UntypedFormArray).clear();
    this.cardsForm.updateValueAndValidity();
    this.cd.detectChanges();
  }

  private resetForm(): void {
    this.cardsForm = this.fb.group({
      transactions: this.fb.array([]),
      period: '',
      search: '',
      customPeriod: '',
      statuses: '',
      pharmacists: '',
      schedules: '',
      inventoryLocationId: this.store.selectSnapshot(InventoryState?.primaryLocationID) || null
    });
  }

  navigateBack() {
    const navigatedFrom = this.navigatedFromPipe.transform(this.navigationState, 'Home', '/dashboard')?.link;
    this.router.navigate([navigatedFrom], {state: {
      isNavigatedBack: true
    }});
  }

  trackByIndex(index) {
    return index;
  }

  excludeTransaction(index: number): void {
    const control = this.getTransactionControl(index);
    const oldValue = control.value;
    if (control && !this.hiddenTransactions.some(x => x.id == control.value.id)) {
      control.patchValue({...oldValue, shouldShow: false});
      this.hiddenTransactions.push({id: control.value.id, position: index});
      this.adjustSOH(oldValue.gpi);
    }
  }

  restoreExcluded(): void { 
    const last = this.hiddenTransactions.pop();
    const control = this.getTransactionControlById(last.id);
    const oldValue = control?.value;
    if (control) {
      control.patchValue({...oldValue, shouldShow: true, filteredOut : false});
      this.applyFilters();
      this.adjustSOH(oldValue.gpi);
    }
  }

  private applyFilters(): void {
    const formValue = this.cardsForm.value;
    const formArray = (this.cardsForm.controls.transactions as UntypedFormArray);
    let transactions = [...(formArray.value as IBulkVerifyPendingTransactionRow []).filter(x => x.shouldShow)];
    if (formValue.customPeriod) {
      transactions = transactions.filter(x => {
        const statusCodeDate = new Date(x.statusCodeDate);
        const customPeriodStart = formValue.customPeriod[0] || new Date(DATE_MIN_STR);
        const customPeriodEnd = formValue.customPeriod[1] ? getEndOfDay(formValue.customPeriod[1]): new Date(DATE_MAX_STR);
        return statusCodeDate >= customPeriodStart && statusCodeDate <= customPeriodEnd;
      });
    } 
    
    if (formValue.period && formValue?.period !== SearchDateRange.Custom) {
       let range: IDateRange = getDateRange(formValue.period);
        transactions = transactions.filter((x) => {
          const statusCodeDate = new Date(x.statusCodeDate);
          return (
            statusCodeDate >= range.startDate && statusCodeDate <= range.endDate
          );
        });
    }

    if (formValue.statuses) {
      transactions = transactions.filter((x) => formValue.statuses?.length ? (formValue.statuses as string[]).includes(x.status) : x);
    }

    if (formValue.pharmacists) {
      transactions = transactions.filter((x) => formValue.pharmacists?.length ? (formValue.pharmacists as string[]).includes(x.pharmacist) : x);
    }

    if (formValue.schedules) {
      transactions = transactions.filter((x) => formValue.schedules?.length ? (formValue.schedules.map(s => s.id) as DrugScheduleEnum[]).includes(x.schedule) : x);
    }

      const word = (<string>formValue.search)?.trim()?.toLowerCase();
      transactions = transactions.filter(x => x.packageNdc.includes(word) || x.packageNdcNoDash.includes(word) || x.friendlyName.toLowerCase().includes(word) || 
                                              x.rxNumber.includes(word) || x.patientName.toLocaleLowerCase().includes(word));

   
    formArray.controls.forEach(transactionControl => {
      const trRowValue = transactionControl.value;
        !transactions.includes(trRowValue) ?
        transactionControl.patchValue({...trRowValue, filteredOut: true}) :
        transactionControl.patchValue({...trRowValue, filteredOut: false});
    });
  }

  private getTransactionControl(index: number): AbstractControl {
    return (this.cardsForm.controls.transactions as UntypedFormArray).controls[index];
  }

  private getTransactionControlById(id: string): AbstractControl {
    const index = (this.cardsForm.controls.transactions as UntypedFormArray).controls.findIndex(x => x.value.id === id);
    if (index > -1)
      return this.getTransactionControl(index);
  }

  getTransactionsToVerifyCount(): number {
    return this.cardsForm.value.transactions?.filter(x => x.shouldShow && !x.filteredOut)?.length || 0;
  }

  showPin(): void {
    this.pinService.setDialogId(this.pinDialogChannel);
    this.pinService.showPinDialog$.next(true);
  }
  now(): Date {
    return new Date();
  }

  focusMultiOptionalDropdown(dropdownName: string): void {
    setTimeout(() => {
      this[dropdownName].opened = true;
      this[dropdownName].isFocused = true;
      this[dropdownName].dropDownOpened.emit();
      this[dropdownName].multiselectDropdown.nativeElement.focus();
    });
  }

  multiselectOpened(limitName: string, dropdownShowName: string): void {
    setTimeout(() => {
      this.statusItemsShowLimit = 1;
      this.pharmacistItemsShowLimit = 1;

      this.isStatusesDropdownOpened = false;
      this.isPharmacistsDropdownOpened = false;

      this[limitName] = 4;
      this[dropdownShowName] = true;
    });
  }

  multiselectClosed(limitName: string, dropdownShowName: string): void {
    this.statusItemsShowLimit = 2;
    this.pharmacistItemsShowLimit = 2;

    this[dropdownShowName] = false;
  }

  searchInputBlurred(): void {
    if (!this.cardsForm.controls.search.value) {
      this.isSearchShrunk = true;
    }
  }

  searchCleared(): void {
    setTimeout(() => {
      this.isSearchShrunk = true
    });
  }

  sortByColumn(columnName: string): void {
   const transactions = this.cardsForm.value.transactions;
   const sorted = this.sort(transactions, columnName);
   this.clearTransactions();
   sorted.map(x => this.appendTransaction(x));
   groupByProperty((transactions as IBulkVerifyPendingTransaction[]).filter(x => !!x.gpi), 'gpi')?.map(x => this.adjustSOH(x.header));
  }
  
  getRxNumber(rowData: IBulkVerifyPendingTransactionRow): string {
    let rx = rowData.rxNumber;  
    if (rowData.partialNumber > 0) {
      rx += ` - ${rowData.partialNumber}`;
    }

    if(this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      rx += ` (${rowData.refillNumber})`;
    }
    return rx;
  }

  close(): void {
    this.store.dispatch(new SetCarouselDisplayList([]));
    this.navigateBack();
  }
}
